class HHSlideshow extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController()
    const { signal } = this.abort
    this.slides = [...this.querySelectorAll('.slideshow__slide')]
    this.dots = [...this.querySelectorAll('[data-index]')]
    this.index = 0
    this.inView = false
    this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)')
    this.videoBreakpoint = matchMedia('(max-width: 749px)')
    this.paused = this.reducedMotion.matches
    this.hovered = false
    this.focused = false
    this.editorSelected = false
    this.motion = this.querySelector('.slideshow__motion')
    this.hasMotion = this.dataset.autoplay === 'true' && this.slides.length > 1 || !!this.querySelector('video')
    this.querySelectorAll('.slideshow__arrows, .slideshow__controls').forEach(el => { el.hidden = false })
    this.motion.hidden = !this.hasMotion
    this.addEventListener('click', event => {
      const arrow = event.target.closest('[data-direction]')
      const dot = event.target.closest('[data-index]')
      if (arrow) this.go(this.index + Number(arrow.dataset.direction))
      if (dot) this.go(Number(dot.dataset.index))
      if (event.target.closest('.slideshow__motion')) {
        this.paused = !this.paused
        this.sync()
      }
    }, { signal })
    this.addEventListener('keydown', event => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key) || /INPUT|TEXTAREA|SELECT/.test(event.target.tagName)) return
      event.preventDefault()
      this.go(this.index + (event.key === 'ArrowRight' ? 1 : -1))
    }, { signal })
    this.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') { this.hovered = true; this.sync() } }, { signal })
    this.addEventListener('pointerleave', () => { this.hovered = false; this.sync() }, { signal })
    this.addEventListener('focusin', () => { this.focused = true; this.sync() }, { signal })
    this.addEventListener('focusout', event => { this.focused = this.contains(event.relatedTarget); this.sync() }, { signal })
    this.addEventListener('touchstart', event => { this.touch = event.changedTouches[0] }, { passive: true, signal })
    this.addEventListener('touchend', event => {
      if (!this.touch) return
      const dx = event.changedTouches[0].clientX - this.touch.clientX
      const dy = event.changedTouches[0].clientY - this.touch.clientY
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) this.go(this.index + (dx < 0 ? 1 : -1))
      this.touch = null
    }, { passive: true, signal })
    document.addEventListener('visibilitychange', () => this.sync(), { signal })
    this.reducedMotion.addEventListener('change', event => { this.paused = event.matches; this.sync() }, { signal })
    this.videoBreakpoint.addEventListener('change', () => {
      this.querySelectorAll('video[data-loaded]').forEach(video => {
        video.classList.remove('is-playing')
        video.load()
      })
      this.sync()
    }, { signal })
    this.addEventListener('shopify:block:select', event => {
      const index = this.slides.indexOf(event.target.closest('.slideshow__slide'))
      if (index < 0) return
      this.editorSelected = true
      this.go(index)
    }, { signal })
    this.addEventListener('shopify:block:deselect', () => { this.editorSelected = false; this.sync() }, { signal })
    this.observer = new IntersectionObserver(entries => {
      this.inView = entries[0].isIntersecting
      this.sync()
    }, { threshold: 0.1 })
    this.observer.observe(this)
    this.header = document.querySelector('hh-header')
    if (this.header && this.dataset.transparentHeader === 'true') {
      this.resize = new ResizeObserver(() => {
        // Keep the original overlap while the sticky header shrinks on scroll.
        if (!document.body.classList.contains('header-sticky')) {
          this.style.setProperty('--slideshow-header-height', `${this.header.getBoundingClientRect().height}px`)
        }
      })
      this.resize.observe(this.header)
    }
    this.sync()
  }

  go(index) {
    if (!this.slides.length) return
    this.index = (index + this.slides.length) % this.slides.length
    this.slides.forEach((slide, i) => {
      const active = i === this.index
      slide.classList.toggle('is-active', active)
      slide.setAttribute('aria-hidden', String(!active))
      slide.inert = !active
    })
    this.dots.forEach((dot, i) => {
      if (i === this.index) dot.setAttribute('aria-current', 'true')
      else dot.removeAttribute('aria-current')
    })
    this.sync()
  }
  waitForPoster(slide, video) {
    const poster = slide.querySelector(
      '.slideshow__media picture img'
    )

    if (!poster) return false

    if (poster.complete && poster.naturalWidth > 0) {
      return false
    }

    if (video.dataset.waitingForPoster === 'true') {
      return true
    }

    video.dataset.waitingForPoster = 'true'

    const resumeVideo = () => {
      delete video.dataset.waitingForPoster

      const resume = () => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.sync()
          })
        })
      }

      if (typeof poster.decode === 'function') {
        poster.decode().catch(() => {}).finally(resume)
      } else {
        resume()
      }
    }

    poster.addEventListener('load', resumeVideo, {
      once: true,
      signal: this.abort.signal,
    })

    poster.addEventListener('error', resumeVideo, {
      once: true,
      signal: this.abort.signal,
    })

    return true
  }
  sync() {
    clearTimeout(this.timer)
    const playing = this.inView && !document.hidden && !this.paused && !this.editorSelected
    const rotating = playing && !this.hovered && !this.focused && this.dataset.autoplay === 'true' && this.slides.length > 1
    this.motion.setAttribute('aria-label', this.paused ? 'Play motion' : 'Pause motion')
    this.motion.setAttribute('aria-pressed', String(this.paused))
    this.motion.firstElementChild.textContent = this.paused ? '▷' : 'Ⅱ'
    this.querySelector('.slideshow__slides').setAttribute('aria-live', rotating ? 'off' : 'polite')
    this.slides.forEach((slide, i) => {
      const video = slide.querySelector('video')
      if (!video) return
      if (i !== this.index || !playing) { video.pause(); return }
      if (!video.dataset.loaded) {
        if (this.waitForPoster(slide, video)) return
        video.querySelectorAll('source').forEach(source => { source.src = source.dataset.src })
        video.load()
        video.dataset.loaded = 'true'
        video.addEventListener('playing', () => video.classList.add('is-playing'), { signal: this.abort.signal })
      }
      video.play()?.catch(() => { video.classList.remove('is-playing') })
    })
    if (rotating) this.timer = setTimeout(() => this.go(this.index + 1), Number(this.dataset.interval) || 5000)
  }

  disconnectedCallback() {
    clearTimeout(this.timer)
    this.abort.abort()
    this.observer.disconnect()
    this.resize?.disconnect()
    this.querySelectorAll('video').forEach(video => video.pause())
  }
}

if (!customElements.get('hh-slideshow')) customElements.define('hh-slideshow', HHSlideshow)
